import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import CitySearchField from "../components/CitySearchField";
import api from "../lib/api";
import ArtistListingCard from "../components/ArtistListingCard";
import ArtistRecommendations from "../components/ArtistRecommendations";
import { loadIndianCityCatalog } from "../lib/indianCities";
import { BUDGET_RANGES } from "../lib/budgetRanges";
import { track } from "../analytics/tracker";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cities, setCities] = useState([]);
  const [popularCities, setPopularCities] = useState([]);
  const [cityAliases, setCityAliases] = useState({});
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const sugRef = useRef(null);
  const searchRequestId = useRef(0);

  // Iter 74 — Persist search filters so revisiting /discover lands on the
  // last known filter set instead of a blank slate. URL params always win
  // (deep links / shared URLs), then localStorage, then empty defaults.
  const savedFiltersKey = "bt_last_search_filters";
  const savedFilters = React.useMemo(() => {
    try {
      // Skip restore when the URL already carries any filter param — a
      // shared / bookmarked link must remain authoritative.
      const hasUrlFilters = params.get("fresh_search") === "1"
        || ["q","category","city","min_price","max_price","sort","event_type"]
        .some((k) => params.get(k));
      if (hasUrlFilters) return null;
      const raw = localStorage.getItem(savedFiltersKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filters
  const [q, setQ] = useState(params.get("q") || savedFilters?.q || "");
  const [category, setCategory] = useState(params.get("category") || savedFilters?.category || "");
  const [city, setCity] = useState(params.get("city") || savedFilters?.city || "");
  const [minPrice, setMinPrice] = useState(params.get("min_price") || savedFilters?.minPrice || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || savedFilters?.maxPrice || "");
  // Event type comes only from links (homepage stories, occasion picker) and
  // is not remembered between visits: only artists who chose it are shown.
  const [eventType, setEventType] = useState(params.get("event_type") || "");
  const selectedBudgetRange = minPrice || maxPrice
    ? BUDGET_RANGES.find((range) => (
      Number(minPrice || 0) === range.min
      && (range.max === null ? !maxPrice : Number(maxPrice) === range.max)
    ))?.value || ""
    : "";
  const [sort, setSort] = useState(savedFilters?.sort === "rating" ? "relevance" : savedFilters?.sort || "relevance");
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadIndianCityCatalog("event")
      .then(({ popular, cities: remaining, aliases }) => {
        setPopularCities(popular.map((item) => item.name));
        setCities([...popular, ...remaining].map((item) => item.name));
        setCityAliases(aliases);
      })
      .catch((error) => {
        console.warn("Unable to load Indian city options for search.", error);
        setCities([]);
        setPopularCities([]);
        setCityAliases({});
      });
    api.get("/catalog/categories").then((r) => setCategories(r.data));
  }, []);

  const run = async (pg = 1, append = false) => {
    const requestId = ++searchRequestId.current;
    if (append) setLoadingMore(true); else setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (city) p.set("city", city);
    if (minPrice) p.set("min_price", minPrice);
    if (maxPrice) p.set("max_price", maxPrice);
    if (eventType) p.set("event_type", eventType);
    p.set("sort", sort);
    p.set("page", pg);
    p.set("limit", "24");
    // Normalize the URL without adding a second history entry. This keeps
    // browser Back pointed at the page that opened Discover (for example the
    // homepage category rail), rather than an intermediate filter URL.
    if (!append) setParams(p, { replace: true });
    setPage(pg);
    // Iter 74 — Persist current filter snapshot so the next visit to
    // /discover starts on this filter set. Only for base fetches — we
    // don't rewrite storage on infinite scroll.
    if (!append) {
      try {
        localStorage.setItem(savedFiltersKey, JSON.stringify({
          q, category, city, minPrice, maxPrice, sort,
        }));
      } catch { /* private-mode / quota */ }
    }
    try {
      const r = await api.get(`/search/artists?${p.toString()}`);
      if (requestId !== searchRequestId.current) return;
      setItems((current) => append ? [...current, ...r.data.items] : r.data.items);
      setPages(r.data.pages);
      setHasMore(pg < r.data.pages);
    } finally {
      if (requestId === searchRequestId.current) {
        if (append) setLoadingMore(false); else setLoading(false);
      }
    }
  };

  // Filters trigger a fresh page-1 fetch. `run` is redefined every render so
  // including it would loop; it closes over the current filter state, so we
  // list all filter deps explicitly instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(1); }, [category, city, sort, minPrice, maxPrice, eventType]);

  // Sprint 6 — Infinite scroll via IntersectionObserver on a sentinel element
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore && !loading && hasMore) {
        run(page + 1, true);
      }
    }, { rootMargin: "400px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, page, loadingMore, loading]);

  // Live suggestions
  useEffect(() => {
    if (!q || q.length < 2) { setSuggestions(null); return; }
    const t = setTimeout(() => {
      api.get(`/search/suggestions?q=${encodeURIComponent(q)}`).then((r) => setSuggestions(r.data)).catch(() => setSuggestions(null));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div data-testid="search-page">
      <div className="orb orb-1" />
      <Nav />
      <div className="container" style={{ paddingTop: "var(--space-8)", paddingBottom: "var(--space-16)" }}>
        <h1 className="font-serif" style={{ fontSize: 36, fontWeight: 700, marginBottom: "var(--space-1)" }}>
          Discover <span style={{ background: "linear-gradient(135deg, var(--gold-light), var(--gold))", WebkitBackgroundClip: "text", color: "transparent" }}>Artists</span>
        </h1>
        <p className="text-muted mb-24">
          {eventType
            ? `Verified artists who perform at ${eventType} events`
            : "Browse verified artists across India"}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            track("search_performed", {
              filters: {
                q: q || undefined,
                category: category || undefined,
                city: city || undefined,
                min_price: minPrice || undefined,
                max_price: maxPrice || undefined,
                event_type: eventType || undefined,
                sort,
              },
            });
            run(1);
          }}
          className="discover-filters"
          data-testid="search-filters"
        >
          <div style={{ position: "relative" }} ref={sugRef}>
            <input
              className="field-input" placeholder="Search by name, genre, vibe…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-q"
              style={{ width: "100%" }}
            />
            {suggestions && (suggestions.artists?.length || suggestions.categories?.length || suggestions.cities?.length) > 0 && (
              <div className="card" style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, marginTop: "var(--space-1)", maxHeight: 320, overflow: "auto", padding: "var(--space-1)" }} data-testid="search-suggestions">
                {suggestions.artists?.map((a) => (
                  <Link
                    key={a.id}
                    to={`/artist/${a.id}`}
                    onClick={() => track("artist_card_click", { artist_id: a.id, source: "search" })}
                    className="sb-item"
                    style={{ padding: "var(--space-1) 10px", display: "block" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="fw-600">{a.label}</div>
                    <div className="text-muted fs-11">{a.sub}</div>
                  </Link>
                ))}
                {suggestions.categories?.map((c) => (
                  <div key={c.slug} className="sb-item" style={{ padding: "var(--space-1) 10px", cursor: "pointer" }} onClick={() => { setQ(""); setCategory(c.label); setSuggestions(null); }}>
                     {c.label}
                  </div>
                ))}
                {suggestions.cities?.map((c) => (
                  <div key={c.slug} className="sb-item" style={{ padding: "var(--space-1) 10px", cursor: "pointer" }} onClick={() => { setQ(""); setCity(c.label); setSuggestions(null); }}>
                     {c.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="filter-category">
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
          <CitySearchField
            className="field-input"
            city={city}
            cities={cities}
            popularCities={popularCities}
            cityAliases={cityAliases}
            onCityChange={setCity}
            listId="discover-city-options"
            placeholder="Search city"
            data-testid="filter-city"
            style={{ width: "100%" }}
          />
          <select
            className="field-input"
            value={selectedBudgetRange}
            onChange={(e) => {
              const range = BUDGET_RANGES.find((option) => option.value === e.target.value);
              setMinPrice(range ? String(range.min) : "");
              setMaxPrice(range?.max === null ? "" : range ? String(range.max) : "");
            }}
            data-testid="filter-budget"
          >
            <option value="">Your budget</option>
            {BUDGET_RANGES.map((range) => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
          <button className="btn btn-gold" type="submit" data-testid="filter-apply">Search</button>
        </form>

        {/* Iter 74 — Active filter chips. Each active filter renders as a
            removable pill so customers see exactly what's narrowing the
            results and can dismiss individual filters with one tap. */}
        {(() => {
          const chips = [];
          if (eventType) chips.push({ key: "event_type", label: `Event: ${eventType}`, clear: () => setEventType("") });
          if (q) chips.push({ key: "q", label: `“${q}”`, clear: () => setQ("") });
          if (category) chips.push({ key: "category", label: ` ${category}`, clear: () => setCategory("") });
          if (city) chips.push({ key: "city", label: ` ${city}`, clear: () => setCity("") });
          if (minPrice || maxPrice) chips.push({
            key: "price",
            label: `₹ ${minPrice ? Number(minPrice).toLocaleString("en-IN") : "0"}${maxPrice ? "–" + Number(maxPrice).toLocaleString("en-IN") : "+"}`,
            clear: () => { setMinPrice(""); setMaxPrice(""); },
          });
          if (chips.length === 0) return null;
          return (
            <div
              data-testid="active-filter-chips"
              style={{
                display: "flex", flexWrap: "wrap", gap: "var(--space-2)",
                marginBottom: "var(--space-3)", padding: "var(--space-2) 12px",
                borderRadius: 12,
                background: "rgba(212,175,55,0.05)",
                border: "1px solid rgba(212,175,55,0.15)",
              }}
            >
              <span style={{ fontSize: 12, color: "var(--white-muted, rgba(255,255,255,0.55))", alignSelf: "center", marginRight: 4 }}>
                Filters:
              </span>
              {chips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`filter-chip-${c.key}`}
                  onClick={c.clear}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "var(--space-1)",
                    padding: "var(--space-1) 10px var(--space-1) 12px",
                    borderRadius: 999,
                    background: "rgba(212,175,55,0.12)",
                    color: "var(--gold-light, #f5d47a)",
                    border: "1px solid rgba(212,175,55,0.3)",
                    fontSize: 12, fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 150ms ease, border-color 150ms ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(212,175,55,0.22)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(212,175,55,0.12)"; }}
                  title="Remove this filter"
                  aria-label={`Remove ${c.label}`}
                >
                  {c.label}
                  <span style={{ marginLeft: 2, opacity: 0.75, fontSize: 13, lineHeight: 1 }}>✕</span>
                </button>
              ))}
            </div>
          );
        })()}

        <div className="flex justify-end items-center mb-16">
          <select className="field-input" style={{ width: 200 }} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort">
            <option value="relevance">Most Relevant</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>

        {loading ? (
          <div className="artist-grid-v2">
            {[...Array(8)].map((_, i) => <div key={`sk-search-${i}`} className="sk-artist-card" data-testid={`sk-search-${i}`} />)}
          </div>
        ) : items.length === 0 ? (
          <>
            <div className="empty">
              <div className="empty-icon"></div>
              <div className="empty-title">No artists found</div>
              <p>Try adjusting your filters, or explore these popular artists.</p>
            </div>
            <ArtistRecommendations />
          </>
        ) : (
          <>
            <div className="artist-grid-v2">
              {items.map((artist) => (
                <ArtistListingCard
                  key={artist.user_id}
                  artist={artist}
                  testId={`artist-card-${artist.user_id}`}
                />
              ))}
            </div>

            {pages > 1 && hasMore && (
              <div ref={sentinelRef} style={{ padding: "var(--space-8)", textAlign: "center" }} data-testid="infinite-scroll-sentinel">
                {loadingMore ? (
                  <div className="artist-grid-v2" style={{ marginTop: "var(--space-3)" }}>
                    {[...Array(4)].map((_, i) => <div key={`m${i}`} className="sk-artist-card" />)}
                  </div>
                ) : (
                  <div className="text-muted fs-13">Scroll to load more…</div>
                )}
              </div>
            )}
            {pages > 1 && !hasMore && (
              <div className="text-muted text-center fs-13" style={{ padding: "var(--space-6)" }} data-testid="infinite-scroll-end">
                — End of results —
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
